import { useEffect, useRef, useState } from 'react';
import { Button, Input } from '../../components/ui';
import { shareAudienceIsLimited, shareAudienceNote } from './shareLinks';
import type { ProjectVisibility } from '../projects/types';

/**
 * "Copy link" — one button, used on a project and on a task.
 *
 * Three things it does that a naive clipboard button does not:
 *
 * 1. It says who can open what you just copied. Kyu chose this over a link
 *    that grants access ("Nothing changes — warn me at copy time"), and the
 *    warning is the whole reason that choice is safe. It appears at the
 *    moment of copying, while you can still change the project's visibility
 *    or add the person, rather than as a complaint from the recipient later.
 *
 * 2. It has a real fallback. navigator.clipboard does not exist outside a
 *    secure context and throws when the document is not focused; a button
 *    that silently does nothing in those cases is worse than no button,
 *    because the person believes they have a link and pastes whatever was
 *    already on their clipboard. On failure the URL is shown in a selected,
 *    read-only field so it can be copied by hand.
 *
 * 3. It confirms, and then stops confirming. "Copied" plus the note for four
 *    seconds, then back to "Copy link" with nothing underneath, so the
 *    control never sits there claiming a state that stopped being true and a
 *    permanent line of grey text never trains people to ignore it.
 */
export function CopyLinkButton({
  url,
  visibility,
  label = 'Copy link',
  className = '',
}: {
  /** The absolute URL to copy. Built by shareLinks.ts, never inline. */
  url: string;
  /**
   * The visibility of the project this link points into, which decides the
   * note. Optional only so the button can be reused somewhere with no
   * project behind it.
   */
  visibility?: ProjectVisibility;
  label?: string;
  className?: string;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'manual'>('idle');
  const manualRef = useRef<HTMLInputElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A pending "Copied" timer on an unmounted panel would set state on a dead
  // component. The task panel closes fast enough for this to be real.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  // Whatever it was showing belongs to the old link.
  useEffect(() => {
    setState('idle');
  }, [url]);

  useEffect(() => {
    if (state === 'manual' && manualRef.current !== null) {
      manualRef.current.focus();
      manualRef.current.select();
    }
  }, [state]);

  async function copy(): Promise<void> {
    try {
      // Optional chaining rather than a truthiness check: older Safari and
      // any page served over plain http have no clipboard object at all.
      if (navigator.clipboard?.writeText === undefined) {
        setState('manual');
        return;
      }
      await navigator.clipboard.writeText(url);
      setState('copied');
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      // Four seconds, not two: the note underneath is a sentence somebody has
      // to read and act on, and it disappears with the confirmation.
      timerRef.current = setTimeout(() => setState('idle'), 4000);
    } catch {
      // Permission refused, document not focused, an extension in the way.
      setState('manual');
    }
  }

  const note = visibility === undefined ? null : shareAudienceNote(visibility);
  const limited = visibility !== undefined && shareAudienceIsLimited(visibility);

  return (
    <div className={`share-link ${className}`.trim()}>
      <div className="share-link-row">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void copy();
          }}
          // The live region is the note below, not the button, so a screen
          // reader hears the outcome once rather than on every re-render.
          aria-label={`${label} to this ${visibility === undefined ? 'page' : 'item'}`}
        >
          {state === 'copied' ? 'Copied' : label}
        </Button>
      </div>

      {state === 'manual' ? (
        <div className="share-link-manual">
          <Input
            ref={manualRef}
            label="Copy this link"
            readOnly
            value={url}
            onFocus={(event) => event.currentTarget.select()}
          />
          <p className="share-link-note">
            Your browser would not let the Hub reach the clipboard. Copy the link above by hand.
          </p>
        </div>
      ) : null}

      {/* The note appears WHEN YOU COPY and then goes away again. Leaving it
          on screen permanently would put a line of grey text under a header
          button on every project page forever, which is how a warning stops
          being read. Shown after the copy is still before the paste. */}
      {note === null || state === 'idle' ? null : (
        <p className={`share-link-note${limited ? ' share-link-note--limited' : ''}`} role="status">
          {state === 'copied' ? `Link copied. ${note}` : note}
        </p>
      )}
    </div>
  );
}
